import { useEffect, useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Upload, CheckCircle, AlertCircle, Clock, Plus } from 'lucide-react';
import { Label } from '@/components/ui/label';

// Helper to handle empty strings for optional URL fields
const optionalUrl = z.string().optional().refine(
  (val) => !val || val === '' || /^https?:\/\/.+/.test(val),
  { message: 'Invalid URL' }
);

const candidateSchema = z.object({
  first_name: z.string().min(1, 'First name is required').max(100),
  last_name: z.string().min(1, 'Last name is required').max(100),
  email_primary: z.string().email('Invalid email').max(255),
  phone_primary: z.string().max(20).optional().or(z.literal('')),
  city: z.string().max(100).optional().or(z.literal('')),
  address: z.string().max(255).optional().or(z.literal('')),
  linkedin_url: optionalUrl,
  github_url: optionalUrl,
  website_url: optionalUrl,
  desired_salary_min: z.string().optional(),
  desired_salary_max: z.string().optional(),
  desired_salary_currency: z.string().optional(),
  availability_status: z.string().optional(),
  availability_date: z.string().optional().or(z.literal('')),
  employment_status: z.string().optional(),
  preferred_contact_method: z.string().optional(),
});

type CandidateFormData = z.infer<typeof candidateSchema>;

interface CandidateFormProps {
  candidate?: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

const CandidateForm = ({ candidate, open, onOpenChange, onSuccess }: CandidateFormProps) => {
  const { toast } = useToast();
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [resumeUploading, setResumeUploading] = useState(false);
  const [parsingStatus, setParsingStatus] = useState<'pending' | 'processing' | 'completed' | 'failed' | null>(null);
  const [parsingJobId, setParsingJobId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [activeTab, setActiveTab] = useState('basic');
  const [candidateId, setCandidateId] = useState<string | null>(candidate?.id || null);
  const [experience, setExperience] = useState<any[]>([]);
  const [education, setEducation] = useState<any[]>([]);
  const [skills, setSkills] = useState<any[]>([]);
  
  const form = useForm<CandidateFormData>({
    resolver: zodResolver(candidateSchema),
    defaultValues: {
      first_name: '',
      last_name: '',
      email_primary: '',
      phone_primary: '',
      city: '',
      address: '',
      linkedin_url: '',
      github_url: '',
      website_url: '',
      desired_salary_min: '',
      desired_salary_max: '',
      desired_salary_currency: 'USD',
      availability_status: 'available',
      availability_date: '',
      employment_status: 'employed',
      preferred_contact_method: 'email',
    },
  });

  useEffect(() => {
    if (candidate) {
      setCandidateId(candidate.id);
      form.reset({
        first_name: candidate.first_name || '',
        last_name: candidate.last_name || '',
        email_primary: candidate.email_primary || '',
        phone_primary: candidate.phone_primary || '',
        city: candidate.city || '',
        address: candidate.address || '',
        linkedin_url: candidate.linkedin_url || '',
        github_url: candidate.github_url || '',
        website_url: candidate.website_url || '',
        desired_salary_min: candidate.desired_salary_min || '',
        desired_salary_max: candidate.desired_salary_max || '',
        desired_salary_currency: candidate.desired_salary_currency || 'USD',
        availability_status: candidate.availability_status || 'available',
        availability_date: candidate.availability_date || '',
        employment_status: candidate.employment_status || 'employed',
        preferred_contact_method: candidate.preferred_contact_method || 'email',
      });
      fetchCandidateDetails(candidate.id);
    } else {
      setCandidateId(null);
      form.reset();
      setResumeFile(null);
      setParsingStatus(null);
      setParsingJobId(null);
      setExperience([]);
      setEducation([]);
      setSkills([]);
    }
  }, [candidate, form, open]);

  const fetchCandidateDetails = async (id: string) => {
    try {
      const [expRes, eduRes, skillsRes] = await Promise.all([
        supabase.from('candidate_experience').select('*, companies(name)').eq('candidate_id', id),
        supabase.from('candidate_education').select('*').eq('candidate_id', id),
        supabase.from('candidate_skills').select('*, skills(name, type)').eq('candidate_id', id),
      ]);

      if (expRes.data) setExperience(expRes.data);
      if (eduRes.data) setEducation(eduRes.data);
      if (skillsRes.data) setSkills(skillsRes.data);
    } catch (error) {
      console.error('Error fetching candidate details:', error);
    }
  };

  // Subscribe to parsing job status updates
  useEffect(() => {
    if (!parsingJobId) return;

    const channel = supabase
      .channel('parsing-job-updates')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'parsing_jobs',
          filter: `id=eq.${parsingJobId}`
        },
        (payload) => {
          const newStatus = payload.new.status as 'pending' | 'processing' | 'completed' | 'failed';
          setParsingStatus(newStatus);
          
          if (newStatus === 'completed') {
            toast({
              title: 'Resume parsed successfully',
              description: 'Profile details extracted from your resume.',
            });
          } else if (newStatus === 'failed') {
            const errorMsg = (payload.new as any).error_message || 'Unknown error';
            toast({
              title: 'Resume parsing failed',
              description: errorMsg,
              variant: 'destructive',
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [parsingJobId, toast]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        toast({
          title: 'Invalid file type',
          description: 'Please upload a PDF file',
          variant: 'destructive',
        });
        return;
      }
      setResumeFile(file);
    }
  };

  const handleResumeUpload = async () => {
    if (!resumeFile) return;

    setResumeUploading(true);
    try {
      // First, create the candidate if new
      let candidateId = candidate?.id;
      
      if (!candidateId) {
        const formData = form.getValues();
        if (!formData.first_name || !formData.last_name || !formData.email_primary) {
          toast({
            title: 'Missing information',
            description: 'Please fill in at least name and email before uploading resume',
            variant: 'destructive',
          });
          setResumeUploading(false);
          return;
        }

        const { data: candidateData, error: candidateError } = await supabase
          .from('candidates')
          .insert([formData])
          .select()
          .single();

        if (candidateError) throw candidateError;
        candidateId = candidateData.id;
      }

      // Upload resume to storage
      const timestamp = Date.now();
      const fileName = `${timestamp}-${resumeFile.name}`;
      const { error: uploadError } = await supabase.storage
        .from('resumes')
        .upload(fileName, resumeFile);

      if (uploadError) throw uploadError;

      // Create resume record
      const { data: resumeData, error: resumeError } = await supabase
        .from('candidate_resumes')
        .insert([{
          candidate_id: candidateId,
          storage_path: fileName,
          original_filename: resumeFile.name,
          file_size: resumeFile.size,
        }])
        .select()
        .single();

      if (resumeError) throw resumeError;

      // Quick extraction for contact info
      await supabase.functions.invoke('extract-personal-info', {
        body: { fileName }
      });

      // Create parsing job and trigger deep parsing
      const { data: parsingJobData, error: parsingError } = await supabase
        .from('parsing_jobs')
        .insert([{
          candidate_id: candidateId,
          resume_id: resumeData.id,
          status: 'pending'
        }])
        .select()
        .single();

      if (parsingError) throw parsingError;

      setParsingJobId(parsingJobData.id);
      setParsingStatus('pending');

      // Trigger async deep parsing
      supabase.functions.invoke('trigger-deep-parse', {
        body: {
          candidateId,
          resumeId: resumeData.id,
          fileName
        }
      });

      toast({
        title: 'Resume uploaded successfully',
        description: 'Extracting profile information...',
      });

      // If this was a new candidate, call onSuccess to refresh the list
      if (!candidate) {
        setTimeout(() => {
          onSuccess();
        }, 2000);
      }

    } catch (error: any) {
      toast({
        title: 'Upload failed',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setResumeUploading(false);
    }
  };

  const checkForDuplicates = async (data: CandidateFormData): Promise<boolean> => {
    try {
      const { data: existingCandidates, error } = await supabase
        .from('candidates')
        .select('id, first_name, last_name, email_primary')
        .or(`email_primary.eq.${data.email_primary},and(first_name.eq.${data.first_name},last_name.eq.${data.last_name})`)
        .limit(5);

      if (error) throw error;

      if (existingCandidates && existingCandidates.length > 0) {
        const duplicateList = existingCandidates.map(c => `${c.first_name} ${c.last_name} (${c.email_primary})`).join(', ');
        toast({
          title: 'Duplicate candidate found',
          description: `Similar candidate(s) already exist: ${duplicateList}`,
          variant: 'destructive',
        });
        return true;
      }
      return false;
    } catch (error: any) {
      console.error('Error checking duplicates:', error);
      return false;
    }
  };

  const onSubmit = async (data: CandidateFormData) => {
    try {
      if (!candidate) {
        // Check for duplicates before creating
        const isDuplicate = await checkForDuplicates(data);
        if (isDuplicate) {
          return;
        }
      }

      // Prepare data for database - convert salary strings to numbers and clean empty strings
      const dbData = {
        ...data,
        linkedin_url: data.linkedin_url || null,
        github_url: data.github_url || null,
        website_url: data.website_url || null,
        desired_salary_min: data.desired_salary_min ? Number(data.desired_salary_min) : null,
        desired_salary_max: data.desired_salary_max ? Number(data.desired_salary_max) : null,
        availability_date: data.availability_date || null,
      };

      if (candidate) {
        const { error } = await supabase
          .from('candidates')
          .update(dbData)
          .eq('id', candidate.id);
        if (error) throw error;
        toast({ title: 'Candidate updated successfully' });
      } else {
        const { data: newCandidate, error } = await supabase
          .from('candidates')
          .insert([dbData])
          .select()
          .single();
        if (error) throw error;
        setCandidateId(newCandidate.id);
        toast({ title: 'Candidate created successfully' });
      }
      onSuccess();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const getParsingStatusIcon = () => {
    switch (parsingStatus) {
      case 'pending':
        return <Clock className="h-4 w-4 text-muted-foreground" />;
      case 'processing':
        return <Loader2 className="h-4 w-4 animate-spin text-primary" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <AlertCircle className="h-4 w-4 text-destructive" />;
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{candidate ? 'Edit Candidate' : 'Add New Candidate'}</DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="basic">Basic Info</TabsTrigger>
            <TabsTrigger value="resume">Resume</TabsTrigger>
            <TabsTrigger value="experience" disabled={!candidateId}>Experience</TabsTrigger>
            <TabsTrigger value="education" disabled={!candidateId}>Education</TabsTrigger>
            <TabsTrigger value="skills" disabled={!candidateId}>Skills</TabsTrigger>
          </TabsList>

          <TabsContent value="basic" className="space-y-4">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="first_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>First Name *</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="last_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Last Name *</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="email_primary"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email *</FormLabel>
                  <FormControl>
                    <Input type="email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone_primary"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="city"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>City</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Address</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="linkedin_url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>LinkedIn URL</FormLabel>
                  <FormControl>
                    <Input placeholder="https://linkedin.com/in/..." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="github_url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>GitHub URL</FormLabel>
                  <FormControl>
                    <Input placeholder="https://github.com/..." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="website_url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Website URL</FormLabel>
                  <FormControl>
                    <Input placeholder="https://..." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Salary Expectations */}
            <div className="pt-4 border-t">
              <h4 className="font-medium mb-3">Salary Expectations</h4>
              <div className="grid grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="desired_salary_min"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Minimum</FormLabel>
                      <FormControl>
                        <Input type="number" placeholder="50000" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="desired_salary_max"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Maximum</FormLabel>
                      <FormControl>
                        <Input type="number" placeholder="80000" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="desired_salary_currency"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Currency</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select currency" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="USD">USD</SelectItem>
                          <SelectItem value="EUR">EUR</SelectItem>
                          <SelectItem value="GBP">GBP</SelectItem>
                          <SelectItem value="INR">INR</SelectItem>
                          <SelectItem value="CAD">CAD</SelectItem>
                          <SelectItem value="AUD">AUD</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Availability & Status */}
            <div className="pt-4 border-t">
              <h4 className="font-medium mb-3">Availability & Status</h4>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="availability_status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Availability Status</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="available">Available</SelectItem>
                          <SelectItem value="open">Open to Opportunities</SelectItem>
                          <SelectItem value="not_looking">Not Looking</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="availability_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Available From</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="employment_status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Employment Status</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="employed">Employed</SelectItem>
                          <SelectItem value="unemployed">Unemployed</SelectItem>
                          <SelectItem value="freelance">Freelance</SelectItem>
                          <SelectItem value="student">Student</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="preferred_contact_method"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Preferred Contact Method</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select method" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="email">Email</SelectItem>
                          <SelectItem value="phone">Phone</SelectItem>
                          <SelectItem value="linkedin">LinkedIn</SelectItem>
                          <SelectItem value="whatsapp">WhatsApp</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={form.formState.isSubmitting}>
                    {form.formState.isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {candidate ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </Form>
          </TabsContent>

          <TabsContent value="resume" className="space-y-4">
            <div className="space-y-4">
              <div className="border-2 border-dashed rounded-lg p-8 text-center">
                <Upload className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">Upload Resume</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Upload a PDF resume to automatically extract candidate information
                </p>
                
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept=".pdf"
                  className="hidden"
                />
                
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={resumeUploading}
                >
                  <Upload className="mr-2 h-4 w-4" />
                  Select PDF File
                </Button>

                {resumeFile && (
                  <div className="mt-4 p-4 bg-muted rounded-lg">
                    <p className="text-sm font-medium">{resumeFile.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {(resumeFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                    <Button
                      type="button"
                      onClick={handleResumeUpload}
                      disabled={resumeUploading}
                      className="mt-3"
                    >
                      {resumeUploading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        'Upload & Parse Resume'
                      )}
                    </Button>
                  </div>
                )}
              </div>

              {parsingStatus && (
                <div className="p-4 border rounded-lg bg-card">
                  <div className="flex items-center gap-2 mb-2">
                    {getParsingStatusIcon()}
                    <span className="font-medium">Parsing Status</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {parsingStatus === 'pending' && 'Waiting to start parsing...'}
                    {parsingStatus === 'processing' && 'Extracting information from your resume...'}
                    {parsingStatus === 'completed' && 'Resume parsed successfully! Profile information has been extracted.'}
                    {parsingStatus === 'failed' && 'Failed to parse resume. You can still fill in the information manually.'}
                  </p>
                </div>
              )}

              <div className="text-sm text-muted-foreground space-y-2">
                <p className="font-medium">What we extract from your resume:</p>
                <ul className="list-disc list-inside space-y-1 pl-2">
                  <li>Contact information (name, email, phone, location)</li>
                  <li>Work experience and job history</li>
                  <li>Education and qualifications</li>
                  <li>Skills and technologies</li>
                  <li>Projects and achievements</li>
                </ul>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  Close
                </Button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="experience" className="space-y-4">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Work Experience</h3>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => {
                    const newExp = {
                      id: `temp-${Date.now()}`,
                      candidate_id: candidateId,
                      job_title: '',
                      company_id: null,
                      start_date: '',
                      end_date: '',
                      is_current: false,
                      description: '',
                    };
                    setExperience([...experience, newExp]);
                  }}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add Experience
                </Button>
              </div>

              {experience.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No work experience added yet. Click "Add Experience" to get started.
                </p>
              ) : (
                <div className="space-y-4">
                  {experience.map((exp, idx) => (
                    <Card key={exp.id} className="p-4">
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label>Job Title *</Label>
                            <Input
                              value={exp.job_title}
                              onChange={(e) => {
                                const updated = [...experience];
                                updated[idx].job_title = e.target.value;
                                setExperience(updated);
                              }}
                            />
                          </div>
                          <div>
                            <Label>Company</Label>
                            <Input
                              value={exp.companies?.name || ''}
                              placeholder="Company name"
                              onChange={(e) => {
                                const updated = [...experience];
                                updated[idx].company_name = e.target.value;
                                setExperience(updated);
                              }}
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <Label>Start Date</Label>
                            <Input
                              type="date"
                              value={exp.start_date || ''}
                              onChange={(e) => {
                                const updated = [...experience];
                                updated[idx].start_date = e.target.value;
                                setExperience(updated);
                              }}
                            />
                          </div>
                          <div>
                            <Label>End Date</Label>
                            <Input
                              type="date"
                              value={exp.end_date || ''}
                              disabled={exp.is_current}
                              onChange={(e) => {
                                const updated = [...experience];
                                updated[idx].end_date = e.target.value;
                                setExperience(updated);
                              }}
                            />
                          </div>
                          <div className="flex items-end">
                            <label className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={exp.is_current}
                                onChange={(e) => {
                                  const updated = [...experience];
                                  updated[idx].is_current = e.target.checked;
                                  if (e.target.checked) updated[idx].end_date = null;
                                  setExperience(updated);
                                }}
                              />
                              <span className="text-sm">Current</span>
                            </label>
                          </div>
                        </div>
                        <div>
                          <Label>Description</Label>
                          <Textarea
                            value={exp.description || ''}
                            onChange={(e) => {
                              const updated = [...experience];
                              updated[idx].description = e.target.value;
                              setExperience(updated);
                            }}
                            rows={3}
                          />
                        </div>
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          onClick={async () => {
                            if (exp.id.toString().startsWith('temp-')) {
                              setExperience(experience.filter((_, i) => i !== idx));
                            } else {
                              await supabase.from('candidate_experience').delete().eq('id', exp.id);
                              setExperience(experience.filter((_, i) => i !== idx));
                              toast({ title: 'Experience deleted' });
                            }
                          }}
                        >
                          Remove
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4">
                <Button
                  type="button"
                  onClick={async () => {
                    try {
                      for (const exp of experience) {
                        let companyId = exp.company_id;
                        
                        // Handle company name - find or create company
                        const companyName = exp.company_name || exp.companies?.name;
                        if (companyName && companyName.trim()) {
                          const { data: existingCompany } = await supabase
                            .from('companies')
                            .select('id')
                            .ilike('name', companyName.trim())
                            .single();

                          if (existingCompany) {
                            companyId = existingCompany.id;
                          } else {
                            const { data: newCompany, error: companyError } = await supabase
                              .from('companies')
                              .insert([{ name: companyName.trim() }])
                              .select()
                              .single();
                            
                            if (companyError) throw companyError;
                            companyId = newCompany.id;
                          }
                        }

                        // Save experience with company_id
                        if (exp.id.toString().startsWith('temp-')) {
                          const { id, companies, company_name, ...expData } = exp;
                          await supabase.from('candidate_experience').insert([{
                            ...expData,
                            company_id: companyId
                          }]);
                        } else {
                          const { companies, company_name, ...expData } = exp;
                          await supabase.from('candidate_experience').update({
                            ...expData,
                            company_id: companyId
                          }).eq('id', exp.id);
                        }
                      }
                      toast({ title: 'Experience saved successfully' });
                      if (candidateId) fetchCandidateDetails(candidateId);
                    } catch (error: any) {
                      toast({ title: 'Error', description: error.message, variant: 'destructive' });
                    }
                  }}
                >
                  Save Experience
                </Button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="education" className="space-y-4">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Education</h3>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => {
                    const newEdu = {
                      id: `temp-${Date.now()}`,
                      candidate_id: candidateId,
                      institution_name: '',
                      degree: '',
                      field_of_study: '',
                      start_date: '',
                      end_date: '',
                      grade: '',
                    };
                    setEducation([...education, newEdu]);
                  }}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add Education
                </Button>
              </div>

              {education.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No education added yet. Click "Add Education" to get started.
                </p>
              ) : (
                <div className="space-y-4">
                  {education.map((edu, idx) => (
                    <Card key={edu.id} className="p-4">
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label>Institution *</Label>
                            <Input
                              value={edu.institution_name}
                              onChange={(e) => {
                                const updated = [...education];
                                updated[idx].institution_name = e.target.value;
                                setEducation(updated);
                              }}
                            />
                          </div>
                          <div>
                            <Label>Degree</Label>
                            <Input
                              value={edu.degree || ''}
                              onChange={(e) => {
                                const updated = [...education];
                                updated[idx].degree = e.target.value;
                                setEducation(updated);
                              }}
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label>Field of Study</Label>
                            <Input
                              value={edu.field_of_study || ''}
                              onChange={(e) => {
                                const updated = [...education];
                                updated[idx].field_of_study = e.target.value;
                                setEducation(updated);
                              }}
                            />
                          </div>
                          <div>
                            <Label>Grade</Label>
                            <Input
                              value={edu.grade || ''}
                              onChange={(e) => {
                                const updated = [...education];
                                updated[idx].grade = e.target.value;
                                setEducation(updated);
                              }}
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label>Start Date</Label>
                            <Input
                              type="date"
                              value={edu.start_date || ''}
                              onChange={(e) => {
                                const updated = [...education];
                                updated[idx].start_date = e.target.value;
                                setEducation(updated);
                              }}
                            />
                          </div>
                          <div>
                            <Label>End Date</Label>
                            <Input
                              type="date"
                              value={edu.end_date || ''}
                              onChange={(e) => {
                                const updated = [...education];
                                updated[idx].end_date = e.target.value;
                                setEducation(updated);
                              }}
                            />
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          onClick={async () => {
                            if (edu.id.toString().startsWith('temp-')) {
                              setEducation(education.filter((_, i) => i !== idx));
                            } else {
                              await supabase.from('candidate_education').delete().eq('id', edu.id);
                              setEducation(education.filter((_, i) => i !== idx));
                              toast({ title: 'Education deleted' });
                            }
                          }}
                        >
                          Remove
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4">
                <Button
                  type="button"
                  onClick={async () => {
                    try {
                      for (const edu of education) {
                        if (edu.id.toString().startsWith('temp-')) {
                          const { id, ...eduData } = edu;
                          await supabase.from('candidate_education').insert([eduData]);
                        } else {
                          await supabase.from('candidate_education').update(edu).eq('id', edu.id);
                        }
                      }
                      toast({ title: 'Education saved successfully' });
                      if (candidateId) fetchCandidateDetails(candidateId);
                    } catch (error: any) {
                      toast({ title: 'Error', description: error.message, variant: 'destructive' });
                    }
                  }}
                >
                  Save Education
                </Button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="skills" className="space-y-4">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Skills</h3>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => {
                    const newSkill = {
                      id: `temp-${Date.now()}`,
                      candidate_id: candidateId,
                      skill_name: '',
                      proficiency_level: 'intermediate',
                      years_experience: null,
                    };
                    setSkills([...skills, newSkill]);
                  }}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add Skill
                </Button>
              </div>

              {skills.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No skills added yet. Click "Add Skill" to get started.
                </p>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  {skills.map((skill, idx) => (
                    <Card key={skill.id} className="p-4">
                      <div className="space-y-3">
                        <div>
                          <Label>Skill Name *</Label>
                          <Input
                            value={skill.skills?.name || skill.skill_name || ''}
                            onChange={(e) => {
                              const updated = [...skills];
                              updated[idx].skill_name = e.target.value;
                              setSkills(updated);
                            }}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label>Level</Label>
                            <select
                              value={skill.proficiency_level}
                              onChange={(e) => {
                                const updated = [...skills];
                                updated[idx].proficiency_level = e.target.value;
                                setSkills(updated);
                              }}
                              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            >
                              <option value="beginner">Beginner</option>
                              <option value="intermediate">Intermediate</option>
                              <option value="advanced">Advanced</option>
                              <option value="expert">Expert</option>
                            </select>
                          </div>
                          <div>
                            <Label>Years</Label>
                            <Input
                              type="number"
                              value={skill.years_experience || ''}
                              onChange={(e) => {
                                const updated = [...skills];
                                updated[idx].years_experience = parseFloat(e.target.value) || null;
                                setSkills(updated);
                              }}
                            />
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          onClick={async () => {
                            if (skill.id.toString().startsWith('temp-')) {
                              setSkills(skills.filter((_, i) => i !== idx));
                            } else {
                              await supabase.from('candidate_skills').delete().eq('id', skill.id);
                              setSkills(skills.filter((_, i) => i !== idx));
                              toast({ title: 'Skill deleted' });
                            }
                          }}
                        >
                          Remove
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4">
                <Button
                  type="button"
                  onClick={async () => {
                    try {
                      for (const skill of skills) {
                        // Find or create skill
                        const skillName = skill.skills?.name || skill.skill_name;
                        const { data: existingSkill } = await supabase
                          .from('skills')
                          .select('id')
                          .ilike('name', skillName)
                          .single();

                        let skillId = existingSkill?.id;
                        if (!skillId) {
                          const { data: newSkill } = await supabase
                            .from('skills')
                            .insert([{ name: skillName, type: 'technical' }])
                            .select()
                            .single();
                          skillId = newSkill?.id;
                        }

                        if (skill.id.toString().startsWith('temp-')) {
                          await supabase.from('candidate_skills').insert([{
                            candidate_id: candidateId,
                            skill_id: skillId,
                            proficiency_level: skill.proficiency_level,
                            years_experience: skill.years_experience,
                          }]);
                        } else {
                          await supabase.from('candidate_skills').update({
                            skill_id: skillId,
                            proficiency_level: skill.proficiency_level,
                            years_experience: skill.years_experience,
                          }).eq('id', skill.id);
                        }
                      }
                      toast({ title: 'Skills saved successfully' });
                      if (candidateId) fetchCandidateDetails(candidateId);
                    } catch (error: any) {
                      toast({ title: 'Error', description: error.message, variant: 'destructive' });
                    }
                  }}
                >
                  Save Skills
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default CandidateForm;
